// Spec 45 component-behavior — cmp_ds_progress + aria_progressbar.
//
// Determinate progress indicator. Two display modes:
//   * linear — default; bar that fills left→right.
//   * circular — ring-shaped progress.
//
// value is REQUIRED (caller's hand on the value). When value is null
// (loading-without-known-percentage), use Spinner instead — Progress
// is for known-duration operations per spec 43 loading_state AC.
//
// a11y per WAI-ARIA aria_progressbar pattern:
//   * role='progressbar'
//   * aria-valuenow + aria-valuemin + aria-valuemax (numbers)
//   * aria-valuetext when the formatted value differs from the bare
//     number ("3 of 10 steps complete" vs "3").
//   * aria-label REQUIRED to describe what's progressing.

import { forwardRef, type CSSProperties, type HTMLAttributes } from "react";

import { cx } from "../lib/cx.ts";
import { isDevGuardActive } from "../lib/dev-guard.ts";

export type ProgressMode = "linear" | "circular";

export interface ProgressProps extends Omit<HTMLAttributes<HTMLDivElement>, "aria-valuenow" | "aria-valuemin" | "aria-valuemax"> {
  readonly mode?: ProgressMode;
  /** Current value within [min, max]. */
  readonly value: number;
  readonly min?: number;
  readonly max?: number;
  /** Required accessible label — what is making progress? */
  readonly "aria-label": string;
  /** Optional formatted value text for screenreaders ("3 of 10 steps"). */
  readonly "aria-valuetext"?: string;
}

export const Progress = forwardRef<HTMLDivElement, ProgressProps>(
  (
    {
      mode = "linear",
      value,
      min = 0,
      max = 100,
      "aria-label": ariaLabel,
      "aria-valuetext": ariaValueText,
      className,
      style,
      ...rest
    },
    ref,
  ) => {
    if (isDevGuardActive() && (typeof ariaLabel !== "string" || ariaLabel.trim().length === 0)) {
      throw new Error(
        "<Progress> requires a non-empty aria-label describing what is progressing.",
      );
    }
    // Clamp the displayed value to [min, max] so a stale value doesn't
    // produce a 110% bar; aria-valuenow gets the clamped value too.
    const clamped = Math.max(min, Math.min(max, value));
    const percent = max > min ? ((clamped - min) / (max - min)) * 100 : 0;

    return (
      <div
        ref={ref}
        role="progressbar"
        aria-label={ariaLabel}
        aria-valuenow={clamped}
        aria-valuemin={min}
        aria-valuemax={max}
        {...(ariaValueText !== undefined ? { "aria-valuetext": ariaValueText } : {})}
        data-component="progress"
        data-mode={mode}
        // The linear fill and the circular ring in styles.css both read this
        // property. A quoted key, not a computed one: the undeclared-variable
        // gate (token-usage.test.ts) only sees `"--x":` declarations.
        style={{ ...style, "--nai-progress-percent": `${percent}%` } as CSSProperties}
        className={cx("nai-progress", className)}
        {...rest}
      />
    );
  },
);
Progress.displayName = "Progress";
